import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { userInitials } from "@/lib/pipeline";
import Avatar from "@/components/Avatar";
import { isOwner } from "@/lib/roles";
import { LayoutDashboard, BarChart3, Settings, Sun, Archive, LogOut, KeyRound, ChevronDown, List, Activity, Pause, Maximize2, Minimize2, Inbox as InboxIcon, Calendar as CalIcon, Users, HardHat, ShieldCheck, AlarmClock, MoreHorizontal, Building2, Sprout } from "lucide-react";
import { isRecruiting, setWorkspace } from "@/lib/workspace";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import MentionBell from "@/components/MentionBell";
import BriefingButton from "@/components/BriefingButton";
import OutreachBell from "@/components/OutreachBell";
import api from "@/api/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function useMedia(query) {
  const [hit, setHit] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setHit(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [query]);
  return hit;
}

const NavTip = ({ label, show, children }) => {
  if (!show) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom" className="bg-[#0B1424] text-white text-[11px] font-bold border border-white/10">{label}</TooltipContent>
    </Tooltip>
  );
};

const NavLink = ({ to, label, icon: Icon, active, testId, wide }) => (
  <NavTip label={label} show={!wide}>
    <Link
      to={to}
      data-testid={testId}
      aria-label={label}
      className={`group relative shrink-0 flex items-center gap-2 ${wide ? "px-3" : "px-2.5"} py-2 text-[13px] font-semibold tracking-tight transition-colors ${
        active ? "text-white" : "text-white/65 hover:text-white"
      }`}
    >
      <Icon className={`h-4 w-4 transition-colors ${active ? "text-[#FFCB05]" : "text-white/55 group-hover:text-[#FFCB05]"}`} />
      {wide && <span>{label}</span>}
      <span
        className={`absolute left-2 right-2 bottom-0 h-[3px] rounded-full transition-colors ${
          active ? "bg-[#FFCB05]" : "bg-transparent group-hover:bg-[#FFCB05]/40"
        }`}
      />
    </Link>
  </NavTip>
);

function MoreMenu({ items, primary = [], path, wide }) {
  const phone = useMedia("(max-width: 639px)");
  const active = items.some((m) => path === m.to);
  const list = phone ? [...primary, { divider: true, to: "divider" }, ...items] : items;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-more"
          aria-label="More"
          className={`group relative shrink-0 flex items-center gap-2 ${wide ? "px-3" : "px-2.5"} py-2 text-[13px] font-semibold tracking-tight outline-none transition-colors data-[state=open]:text-white ${
            active ? "text-white" : "text-white/65 hover:text-white"
          }`}
        >
          <MoreHorizontal className={`h-4 w-4 ${active ? "text-[#FFCB05]" : "text-white/55 group-hover:text-[#FFCB05]"}`} />
          {wide && <span>More</span>}
          <ChevronDown className="h-3 w-3 opacity-60 transition-transform group-data-[state=open]:rotate-180" />
          <span className={`absolute left-2 right-2 bottom-0 h-[3px] rounded-full ${active ? "bg-[#FFCB05]" : "bg-transparent"}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent data-testid="nav-more-menu" align="start" sideOffset={8} className="w-52 bg-white p-1 border-[#15233B]/10 shadow-xl">
        {list.map((n) => n.divider ? (
          <div key="divider" className="my-1 h-px bg-[#15233B]/10" />
        ) : (
          <DropdownMenuItem key={n.to} asChild>
            <Link
              to={n.to}
              data-testid={phone && primary.includes(n) ? `${n.testId}-mobile` : n.testId}
              className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer focus:bg-[#FFF9C4] ${path === n.to ? "text-[#15233B] font-bold bg-[#FFF9C4]/60" : "text-[#15233B]/80"}`}
            >
              <n.icon className="h-4 w-4 text-[#15233B]/50" /> {n.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const path = loc.pathname;
  const [open, setOpen] = useState(false);
  const wide = useMedia("(min-width: 1660px)");
  const [showChange, setShowChange] = useState(false);
  const [compact, setCompact] = useState(() => localStorage.getItem("avida_density") === "compact");
  const [outreachDue, setOutreachDue] = useState(0);
  useEffect(() => {
    document.body.classList.toggle("density-compact", compact);
    localStorage.setItem("avida_density", compact ? "compact" : "normal");
  }, [compact]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-profile-menu]")) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    if (!user || user.role === "field_agent") return;
    const load = () => api.get("/outreach/summary").then(({ data }) => setOutreachDue(data.needs_attention || 0)).catch(() => {});
    load();
    window.addEventListener("avida-outreach-updated", load);
    window.addEventListener("focus", load);
    return () => {
      window.removeEventListener("avida-outreach-updated", load);
      window.removeEventListener("focus", load);
    };
  }, [user]);

  const staff = user?.role === "staff";
  const recruiting = isRecruiting();
  const onOutreach = path === "/outreach";
  const primary = [
    { to: "/my-day", label: "My Day", icon: Sun, testId: "nav-my-day" },
    ...(recruiting ? [{ to: "/prospects", label: "Prospects", icon: Sprout, testId: "nav-prospects" }] : []),
    { to: "/", label: "Board", icon: LayoutDashboard, testId: "nav-board" },
    { to: "/clients", label: "Clients", icon: List, testId: "nav-clients" },
    ...(!staff
      ? [
          { to: "/overdue", label: "Overdue", icon: AlarmClock, testId: "nav-overdue" },
          { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports" },
        ]
      : []),
  ];
  const more = [
    ...(staff
      ? [
          { to: "/overdue", label: "Overdue", icon: AlarmClock, testId: "nav-overdue" },
          { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports" },
        ]
      : []),
    { to: "/inbox", label: "Inbox", icon: InboxIcon, testId: "nav-inbox" },
    { to: "/calendar", label: "Calendar", icon: CalIcon, testId: "nav-calendar" },
    { to: "/stuck", label: "Stuck", icon: Pause, testId: "nav-stuck" },
    { to: "/partners", label: "Partners", icon: Users, testId: "nav-partners" },
    { to: "/outreach", label: "Partner Outreach", icon: Building2, testId: "nav-outreach" },
    { to: "/pcws", label: "PCWs", icon: HardHat, testId: "nav-pcws" },
    { to: "/compliance", label: "Compliance", icon: ShieldCheck, testId: "nav-compliance" },
    { to: "/archived", label: "Archived", icon: Archive, testId: "nav-archived" },
    { to: "/activity", label: "Activity", icon: Activity, testId: "nav-activity" },
  ];

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 w-full bg-[#15233B] text-white shadow-lg"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_15%_40%,#FFCB05_0,transparent_50%),radial-gradient(circle_at_80%_60%,#1E73C9_0,transparent_55%)]"
      />
      <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4 relative">
        <Link to="/" data-testid="brand-home-link" className="flex shrink-0 items-center gap-3 hover:opacity-90">
          <div className="h-11 w-11 grid place-items-center">
            <img src="/brand/avida-icon.png" alt="Avida" className="h-full w-auto object-contain" draggable={false} />
          </div>
          <div className="leading-tight hidden xl:block">
            <div className="text-base font-extrabold tracking-tight text-white">Avida</div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#FFCB05]/90">Personal Care</div>
          </div>
        </Link>

        {user?.role !== "field_agent" && (
          <div data-testid="workspace-switcher" className="hidden sm:flex items-center rounded-full border border-white/20 overflow-hidden shrink-0">
            <button
              data-testid="ws-clients"
              onClick={() => { setWorkspace("intake"); window.location.assign("/"); }}
              className={`px-2 xl:px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] transition-colors ${!recruiting && !onOutreach ? "bg-[#FFCB05] text-[#15233B]" : "text-white/60 hover:text-white"}`}
            >
              Clients
            </button>
            <button
              data-testid="ws-recruiting"
              onClick={() => { setWorkspace("recruiting"); window.location.assign("/"); }}
              className={`px-2 xl:px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] transition-colors ${recruiting && !onOutreach ? "bg-[#FFCB05] text-[#15233B]" : "text-white/60 hover:text-white"}`}
            >
              Recruiting
            </button>
            <button
              data-testid="ws-partners"
              onClick={() => nav("/outreach")}
              title="Partner Outreach — referral organizations & follow-ups"
              className={`px-2 xl:px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] transition-colors flex items-center gap-1 ${onOutreach ? "bg-[#FFCB05] text-[#15233B]" : "text-white/60 hover:text-white"}`}
            >
              Partners
              {outreachDue > 0 && (
                <span data-testid="ws-partners-count" className="min-w-[15px] h-[15px] px-1 grid place-items-center rounded-full bg-[#b91c1c] text-white text-[8px] font-extrabold leading-none">{outreachDue}</span>
              )}
            </button>
          </div>
        )}

        <TooltipProvider delayDuration={150}>
          <div className="ml-1 md:ml-4 flex items-center min-w-0">
            <nav data-testid="main-nav" className={`${user?.role === "field_agent" ? "flex" : "hidden sm:flex"} items-center gap-0.5 overflow-x-auto no-scrollbar min-w-0`}>
              {user?.role === "field_agent" ? (
                <NavLink to="/field" label="My Clients" icon={InboxIcon} active={path === "/field"} testId="nav-field" wide />
              ) : (
                primary.map((n) => (
                  <NavLink key={n.to} to={n.to} label={n.label} icon={n.icon} active={path === n.to} testId={n.testId} wide={wide} />
                ))
              )}
            </nav>
            {user?.role !== "field_agent" && <MoreMenu items={more} primary={primary} path={path} wide={wide} />}
          </div>
        </TooltipProvider>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {user?.role !== "field_agent" && <BriefingButton />}
          {user?.role !== "field_agent" && <span className="sm:hidden flex"><OutreachBell /></span>}
          {user?.role !== "field_agent" && <MentionBell />}
          <button
            onClick={() => setCompact((p) => !p)}
            data-testid="density-toggle"
            title={compact ? "Switch to comfortable density" : "Switch to compact density"}
            className="hidden sm:inline-flex p-2 rounded-md text-white/65 hover:text-[#FFCB05] hover:bg-white/10"
          >
            {compact ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </button>
          <div className="relative" data-profile-menu>
            <button
              data-testid="profile-menu-button"
              onClick={(e) => { e.stopPropagation(); setOpen((p) => !p); }}
              className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-white/10"
            >
              <div className="text-right hidden sm:block leading-tight">
                <div className="text-sm font-bold text-white" data-testid="header-user-name">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFCB05]/90">{user?.role}</div>
              </div>
              {user?.avatar ? (
                <Avatar user={user} size={36} className="ring-2 ring-white/20" />
              ) : (
                <div className="h-9 w-9 rounded-full bg-[#FFCB05] grid place-items-center text-sm font-extrabold text-[#15233B] ring-2 ring-white/20">
                  {userInitials(user?.name || "?")}
                </div>
              )}
              <ChevronDown className="h-3.5 w-3.5 text-white/65" />
            </button>
            {open && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-xl border border-[#15233B]/10 overflow-hidden z-50" data-testid="profile-menu">
                <div className="px-3 py-2 border-b border-[#15233B]/10 bg-[#15233B] text-white">
                  <div className="text-xs text-[#FFCB05]">Signed in as</div>
                  <div className="text-sm font-bold truncate">{user?.email}</div>
                </div>
                {isOwner(user) && (
                  <button
                    data-testid="nav-settings"
                    onClick={() => { nav("/settings"); setOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm text-[#15233B] hover:bg-[#FFF9C4] flex items-center gap-2 ${path === "/settings" ? "font-bold bg-[#FFF9C4]/60" : ""}`}
                  >
                    <Settings className="h-4 w-4" /> Settings
                  </button>
                )}
                <button
                  data-testid="open-change-password"
                  onClick={() => { setShowChange(true); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm text-[#15233B] hover:bg-[#FFF9C4] flex items-center gap-2"
                >
                  <KeyRound className="h-4 w-4" /> Change password
                </button>
                <button
                  data-testid="open-security-activity"
                  onClick={() => { nav("/security"); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm text-[#15233B] hover:bg-[#FFF9C4] flex items-center gap-2"
                >
                  <ShieldCheck className="h-4 w-4" /> Sign-in activity
                </button>
                <button
                  data-testid="logout-button"
                  onClick={() => { logout(); nav("/login"); }}
                  className="w-full text-left px-3 py-2 text-sm text-[#b91c1c] hover:bg-[#b91c1c]/5 flex items-center gap-2"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Sunflower hairline accent */}
      <div className="h-[3px] bg-gradient-to-r from-transparent via-[#FFCB05] to-transparent" aria-hidden />
      {showChange && <ChangePasswordDialog onClose={() => setShowChange(false)} />}
    </header>
  );
}
